import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import DatePicker from "react-datepicker"
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'

import { httpOk, GET, PUT, POST } from 'helpers/api';
import { formatDate, formatMoney, regexPhoneNumber } from 'helpers/helpers';

const hours = Array.from({ 'length': 24 }, (_, i) => i);
const timeList = hours.flatMap((hour) =>
	[0, 30].map((minute) => {
		const timeString = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
		return { id: timeString, name: timeString };
	})
);

function timeToMinutes(time) {
	if (!time) return null
	const [hour, minute] = String(time).split(':').map(Number)
	return hour * 60 + minute
}

function minutesToTime(minutes) {
	const value = Math.max(0, Math.min(minutes, 23 * 60 + 30))
	return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`
}

function isSameDay(a, b) {
	return a && b && new Date(a).toDateString() === new Date(b).toDateString()
}

// Набранные цифры → «ЧЧ:ММ» по мере ввода: 1530 → 15:30, 930 → 9:30
function maskTime(input) {
	const digits = String(input).replace(/\D/g, '')
	const hourLength = Number(digits[0]) > 2 ? 1 : 2
	const hour = digits.slice(0, hourLength)
	const minute = digits.slice(hourLength, hourLength + 2)
	return minute ? `${hour}:${minute}` : hour
}

// Окончательное значение поля: «15» → 15:00, «15:3» → 15:30; невалидное → null
function parseTime(text) {
	if (!text) return ''
	const [hourPart, minutePart = ''] = maskTime(text).split(':')
	const hour = Number(hourPart)
	const minute = Number(minutePart.padEnd(2, '0'))
	if (hourPart === '' || hour > 23 || minute > 59) return null
	return minutesToTime(hour * 60 + minute)
}

// Поле времени: печатается цифрами без двоеточия, рядом список получасов.
// Список открывается не с 00:00, а вокруг нужного часа — выбранного или подсказанного
function TimeField({ value, onChange, options, centerOn, min = null }) {
	const [text, setText] = useState(value || '')
	const [open, setOpen] = useState(false)
	const [menuStyle, setMenuStyle] = useState({})
	const inputRef = useRef(null)
	const listRef = useRef(null)

	const typed = parseTime(text)
	const anchor = typed || value || centerOn
	const anchorSlot = anchor ? minutesToTime(Math.floor(timeToMinutes(anchor) / 30) * 30) : null

	useEffect(() => {
		setText(value || '')
	}, [value])

	useEffect(() => {
		if (!open || !listRef.current || !anchorSlot) return
		const list = listRef.current
		const option = list.querySelector(`[data-time="${anchorSlot}"]`)
		if (option) list.scrollTop = option.offsetTop - list.clientHeight / 2 + option.offsetHeight / 2
	}, [open, anchorSlot])

	function openMenu() {
		const rect = inputRef.current.getBoundingClientRect()
		const height = 240
		const fitsBelow = window.innerHeight - rect.bottom > height + 8
		setMenuStyle({
			'left': rect.left,
			'width': rect.width,
			'top': fitsBelow ? rect.bottom + 4 : rect.top - height - 4,
			'maxHeight': height,
		})
		setOpen(true)
	}

	function commit(time) {
		setOpen(false)
		const parsed = parseTime(time)
		const tooEarly = parsed && min !== null && timeToMinutes(parsed) <= min
		if (parsed === null || tooEarly) {
			setText(value || '')
			return
		}
		setText(parsed)
		if (parsed !== value) onChange(parsed)
	}

	return (
		<div className="reservation-time-field">
			<input
				ref={inputRef}
				type="text"
				inputMode="numeric"
				className="form-control"
				placeholder="--:--"
				value={text}
				onFocus={(e) => {
					e.target.select()
					openMenu()
				}}
				onChange={(e) => setText(maskTime(e.target.value))}
				onBlur={() => commit(text)}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						e.preventDefault()
						commit(text)
					}
					if (e.key === 'Escape') {
						e.stopPropagation()
						setText(value || '')
						setOpen(false)
					}
				}}
			/>
			{!!value &&
				<button type="button" className="reservation-time-clear" tabIndex={-1}
					onMouseDown={(e) => {
						e.preventDefault()
						setText('')
						onChange('')
					}}>
					<i className="uil uil-times"></i>
				</button>
			}
			{open && createPortal(
				<div className="reservation-time-menu" style={menuStyle} ref={listRef}>
					{options.map(option => (
						<div key={option.id} data-time={option.id}
							className={`reservation-time-option ${option.id === value ? 'selected' : ''} ${option.id === anchorSlot ? 'anchor' : ''}`}
							onMouseDown={(e) => {
								// Не даём инпуту потерять фокус раньше выбора — иначе blur закоммитит набранный текст
								e.preventDefault()
								commit(option.id)
							}}>
							{option.name}
						</div>
					))}
				</div>,
				document.body
			)}
		</div>
	)
}

// pending — заявка сервиса бронирования, ждёт решения заведения; no_show — гость не пришёл;
// declined — заявку отклонили
const reservationStatuses = ['pending', 'created', 'opened', 'closed', 'cancelled', 'no_show', 'declined']
// Стол освобождают — в сетке не показываем
const releasedStatuses = ['cancelled', 'no_show', 'declined']

function reservationStatusLabel(t, status) {
	if (status === 'pending') return t('reservation_status_pending')
	if (status === 'no_show') return t('reservation_status_no_show')
	if (status === 'declined') return t('reservation_status_declined')
	return t(status)
}

/** Сервис бронирования — названием его ключа, свои источники — подписью */
function reservationSourceLabel(t, item) {
	if (!item?.source) return ''
	if (item.source === 'partner') return item.source_name || 'API'
	return t('reservation_source_' + item.source)
}

function emptyForm(prefix) {
	return {
		'phone': prefix,
		'client_id': null,
		'client_name': '',
		'note': '',
		'reservation_date': new Date(),
		'reservation_start_time': '',
		'reservation_end_time': '',
		'hall_id': null,
		'table_id': null,
		'table_name': '',
		'people_number': '',
	}
}

function Index({ client = {}, clientPhone = '' }) {
	const { t } = useTranslation()
	const reduxAccount = useSelector(state => state.account)
	// Префикс в настройках пользователя приходит числом (998)
	const phonePrefix = String(reduxAccount?.prefix ?? '')
	const disableRef = useRef(false)

	const [data, setData] = useState([])
	const [halls, setHalls] = useState([])
	const [activeHallId, setActiveHallId] = useState(null)
	const [activeTab, setActiveTab] = useState('time')
	const [hoveredSlot, setHoveredSlot] = useState(null)
	const [selectedItem, setSelectedItem] = useState({})
	const [modalStatus, setModalStatus] = useState(false)
	const [modalCreate, setModalCreate] = useState(false)
	const [statusFilter, setStatusFilter] = useState([])
	const [form, setForm] = useState(emptyForm(phonePrefix))
	const [filter, setFilter] = useState({ 'reservation_date': new Date() })
	// Отказ по заявке — с причиной: её увидит сервис бронирования и гость
	const [declineReason, setDeclineReason] = useState(null)

	const activeHall = halls.find(item => item.id === activeHallId) || halls[0]
	const tables = activeHall?.tables || []
	const formHall = halls.find(item => item.id === form.hall_id) || halls[0]
	const activeReservations = data.filter(item => !releasedStatuses.includes(item.status))
	// Ничего не отмечено — показываем все статусы
	const listData = statusFilter.length ? data.filter(item => statusFilter.includes(item.status)) : data

	async function getData() {
		var filterCopy = { ...filter }
		filterCopy.reservation_date = formatDate(filterCopy.reservation_date, 'yyyy-MM-dd')
		const response = await GET('/reservations', filterCopy, { loader: true })
		if (httpOk(response)) setData(response.data)

		const responseHalls = await GET('/reservations-halls')
		if (httpOk(responseHalls)) setHalls(responseHalls.data)
	}

	async function searchClient(phone = form.phone) {
		if (!phone || phone.length < 11) return null

		const response = await GET(`/search-client/${phone}`, {}, { loader: true, notification: false })
		if (!httpOk(response) || !response.data?.id) return null

		setForm(prev => ({
			...prev,
			'client_id': response.data.id,
			'client_name': response.data.first_name || prev.client_name,
		}))
		return response.data.id
	}

	async function create() {
		if (disableRef.current) return
		// Стол необязателен: бронь «на заведение» — стол назначит касса или посадит гостя на свободный.
		// А вот число гостей нужно: по нему выбирают стол
		if (!Number(form.people_number)) {
			toast.error(t('reservation_enter_guests'))
			return
		}

		disableRef.current = true

		// Клиент подтягивается по номеру; если оператор не дождался поиска — ищем перед сохранением,
		// иначе сервер отобьёт бронь на "client_id required"
		var clientId = form.client_id
		if (!clientId) clientId = await searchClient(form.phone)
		if (!clientId) {
			toast.error(t('please_create_client'))
			disableRef.current = false
			return
		}

		const response = await POST('/reservations', {
			'reservation_type': 'table',
			'client_id': clientId,
			'client_name': form.client_name,
			'phone': form.phone,
			'note': form.note,
			'hall_id': form.hall_id,
			'table_id': form.table_id,
			'reservation_date': formatDate(form.reservation_date, 'yyyy-MM-dd'),
			'reservation_start_time': form.reservation_start_time,
			'reservation_end_time': form.reservation_end_time,
			'people_number': Number(form.people_number),
		}, { loader: true })

		disableRef.current = false
		if (httpOk(response)) {
			setModalCreate(false)
			getData()
		}
	}

	async function cancelReservation() {
		const response = await PUT('/reservations', {
			'id': selectedItem.id,
			'status': 'cancelled'
		})

		if (httpOk(response)) {
			getData()
			toggleModalStatus()
		}
	}

	/** Заявка сервиса бронирования: подтвердить или отклонить с причиной */
	async function changeStatus(status, reason = null) {
		const response = await PUT('/reservations', {
			'id': selectedItem.id,
			'status': status,
			'reason': reason,
		}, { loader: true })

		if (httpOk(response)) {
			getData()
			toggleModalStatus()
		}
	}

	async function copyPhone() {
		try {
			await navigator.clipboard.writeText(String(selectedItem.client_phone || ''))
			toast.success(t('copied'))
		} catch (error) {
			toast.error(t('error'))
		}
	}

	function toggleModalStatus(bool = false, item = {}) {
		setDeclineReason(null)
		setSelectedItem(item)
		setModalStatus(bool)
	}

	// Слот из сетки приходит с залом, столом и часом — форма открывается уже заполненной
	function openCreate(slot = {}) {
		const hasClient = !!client?.id && String(clientPhone || '').length >= 11
		const hall = halls.find(item => item.id === slot.hall_id) || activeHall
		const startTime = slot.hour !== undefined ? minutesToTime(slot.hour * 60) : ''

		setForm({
			...emptyForm(phonePrefix),
			'phone': hasClient ? String(clientPhone) : phonePrefix,
			'client_id': hasClient ? client.id : null,
			'client_name': hasClient ? (client.first_name || '') : '',
			'reservation_date': filter.reservation_date,
			'hall_id': hall?.id ?? null,
			'table_id': slot.table?.id ?? null,
			'table_name': slot.table?.name ?? '',
			'reservation_start_time': startTime,
		})
		setModalCreate(true)
	}

	function changeDate(days) {
		const newDate = new Date(filter.reservation_date);
		newDate.setDate(newDate.getDate() + days);
		setFilter({ ...filter, 'reservation_date': newDate })
	}

	function returnReservedInfo(tableId, hour) {
		return activeReservations.find(reservation => {
			const startHour = parseInt(reservation.reservation_start_time.split(":")[0]);
			const endHour = reservation.reservation_end_time
				? parseInt(reservation.reservation_end_time.split(":")[0])
				: null;

			return reservation.table_id === tableId &&
				(hour === startHour || (endHour !== null && hour >= startHour && hour < endHour));
		});
	}

	function defaultStartTime() {
		if (!isSameDay(form.reservation_date, new Date())) return '12:00'
		const now = new Date()
		return minutesToTime(Math.ceil((now.getHours() * 60 + now.getMinutes()) / 30) * 30)
	}

	const startMinutes = timeToMinutes(form.reservation_start_time)
	const endTimeList = startMinutes === null
		? timeList
		: timeList.filter(option => timeToMinutes(option.id) > startMinutes)

	useEffect(() => {
		getData()
	}, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

	// Номер набран целиком — сразу ищем гостя, как в кассе
	useEffect(() => {
		if (modalCreate && !form.client_id && form.phone?.length === 12) searchClient()
	}, [form.phone]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="w-100 p-2">
				<div className="card reservation-page" style={{ 'height': 'calc(100vh - 70px)' }}>
					<div className="reservation-body">
						{activeTab === 'time' &&
							<div className="reservation-halls">
								<div className="reservation-halls-summary">
									<div>
										<div className="reservation-halls-summary-label">{t('halls')}</div>
										<div className="reservation-halls-summary-value">
											{halls.length}
											<span>{t('halls_count_label')}</span>
										</div>
									</div>
									<div className="reservation-halls-summary-icon">
										<i className="uil uil-lamp"></i>
									</div>
								</div>
								{halls.map((item) => (
									<div key={item.id}
										className={`reservation-hall-item ${item.id === activeHall?.id ? 'active' : ''}`}
										title={item.name}
										onClick={() => setActiveHallId(item.id)}>
										{item.name}
									</div>
								))}
							</div>
						}

						<div className="reservation-main">
							<div className="reservation-toolbar">
								<div className="booking-date reservation-date">
									<div className="booking-date-btn" onClick={() => changeDate(-1)}>
										<i className="uil-angle-left-b"></i>
									</div>
									<DatePicker
										selected={filter.reservation_date}
										onChange={(date) => setFilter({ ...filter, 'reservation_date': date })}
										withPortal
										dateFormat="dd.MM.yyyy"
										portalId="root-portal"
										className="booking-date-picker"
									/>
									<div className="booking-date-btn" onClick={() => changeDate(1)}>
										<i className="uil-angle-right-b"></i>
									</div>
								</div>
								<div className="d-flex align-items-center gap-3">
									{activeTab === 'all' &&
										<div className="reservation-status-filter">
											{reservationStatuses.map(status => {
												const count = data.filter(item => item.status === status).length
												return (
													<button type="button" key={status}
														className={`reservation-status-chip ${status} ${statusFilter.includes(status) ? 'active' : ''}`}
														onClick={() => setStatusFilter(statusFilter.includes(status)
															? statusFilter.filter(item => item !== status)
															: [...statusFilter, status])}>
														{reservationStatusLabel(t, status)}
														<span>{count}</span>
													</button>
												)
											})}
										</div>
									}
									<div>
										{t('total')}: <b>{activeReservations.length} {t('reservations_count_label')}</b>
									</div>
								</div>
							</div>

							{activeTab === 'time' &&
								<div className="reservation-grid-card">
									<div className="reservation-grid-scroll">
										<table className="reservation-grid">
											<colgroup>
												<col style={{ 'width': '130px' }} />
												{hours.map(hour => <col key={hour} />)}
											</colgroup>
											<thead>
												<tr>
													<th className="reservation-grid-table-head">{t('tables')}</th>
													{hours.map(hour => (
														<th key={hour} className={hoveredSlot?.hour === hour ? 'hovered' : ''}>{hour}</th>
													))}
												</tr>
											</thead>
											<tbody>
												{tables.map((item) => (
													<tr key={item.id}>
														<td className={`reservation-grid-table-name ${hoveredSlot?.tableId === item.id ? 'hovered' : ''}`}>
															<span className="text-truncate">{item.name}</span>
															{!!Number(item.guest_count) &&
																<span className="reservation-guest-count" title={t('reservation_table_capacity')}>
																	{item.guest_count} {t('reservation_seats')}
																</span>
															}
														</td>
														{hours.map(hour => {
															const reservation = returnReservedInfo(item.id, hour)
															return (
																<td key={hour}
																	className={`reservation-grid-slot ${reservation ? 'reserved' : ''}`}
																	title={reservation
																		? `${reservation.reservation_start_time?.substring(0, 5)} ${reservation.client_name || ''}`
																		: ''}
																	onMouseEnter={() => setHoveredSlot({ 'tableId': item.id, hour })}
																	onMouseLeave={() => setHoveredSlot(null)}
																	onClick={() => reservation
																		? toggleModalStatus(true, reservation)
																		: openCreate({ 'hall_id': activeHall?.id, 'table': item, hour })}>
																	{!reservation && <div className="reservation-grid-dot"></div>}
																</td>
															)
														})}
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							}

							{activeTab === 'all' &&
								<div className="reservation-list no-scrollbar">
									{listData.map((item, index) => (
										<div key={item.id ?? index} className="reservation-list-item"
											onClick={() => toggleModalStatus(true, item)}>
											<div className="reservation-list-index">{index + 1}</div>
											<div className="reservation-list-main">
												<div className="reservation-list-name">{item.client_name || '—'}</div>
												<div className="reservation-list-chips">
													<span className="reservation-chip">
														<i className="uil uil-clock"></i>
														{item.reservation_start_time?.substring(0, 5)}
														{!!item.reservation_end_time && `-${item.reservation_end_time.substring(0, 5)}`}
													</span>
													<span className="reservation-chip">
														<i className="uil uil-restaurant"></i>
														{item.table_id ? <>{item.hall_name} / {item.table_name}</> : t('without_table')}
													</span>
													<span className="reservation-chip">
														<i className="uil uil-users-alt"></i>
														{item.people_number || 1}
													</span>
													{!!reservationSourceLabel(t, item) &&
														<span className="reservation-chip">
															<i className="uil uil-link"></i>
															{reservationSourceLabel(t, item)}
														</span>
													}
													{!!item.client_phone &&
														<span className="reservation-chip">
															<i className="uil uil-phone"></i>
															{regexPhoneNumber(item.client_phone)}
														</span>
													}
													{!!item.note &&
														<span className="reservation-chip">
															<i className="uil uil-comment-alt-notes"></i>
															{item.note}
														</span>
													}
												</div>
											</div>
											<div className="reservation-list-side">
												<span className={`reservation-status ${item.status || 'created'}`}>{reservationStatusLabel(t, item.status)}</span>
												{Number(item.amount) > 0 &&
													<div>{t('prepayment')}: <b>{formatMoney(item.amount)}</b></div>
												}
												<div className="reservation-list-author">
													{item.created_by} · {formatDate(item.created_at, 'dd.MM HH:mm')}
												</div>
											</div>
										</div>
									))}
									{!listData.length &&
										<div className="reservation-list-empty">{t('list_empty')}</div>
									}
								</div>
							}
						</div>
					</div>

					<div className="reservation-footer">
						<div className="d-flex gap-2">
							<button type="button"
								className={`reservation-footer-btn ${activeTab === 'time' ? 'active' : ''}`}
								onClick={() => setActiveTab('time')}>
								{t('by_hours')}
							</button>
							<button type="button"
								className={`reservation-footer-btn ${activeTab === 'all' ? 'active' : ''}`}
								onClick={() => setActiveTab('all')}>
								{t('all_reservations')}
							</button>
						</div>
						<button type="button" className="btn btn-primary reservation-footer-create"
							onClick={() => openCreate()}>
							<i className="uil uil-plus me-1"></i>
							{t('create')}
						</button>
					</div>
				</div>
			</div>

			{/* MODAL TABLE STATUS */}
			<Modal show={modalStatus} animation={false} centered
				dialogClassName="reservation-detail-modal"
				onHide={() => toggleModalStatus()}>
				<Modal.Body className="reservation-detail-modal-body">
					<div className="reservation-detail-header">
						<div className="reservation-detail-title">
							<i className="uil uil-calendar-alt"></i>
							<span>{t('table_reservation')}</span>
						</div>
						<button type="button" className="reservation-detail-close" onClick={() => toggleModalStatus()}>
							<i className="uil uil-times"></i>
						</button>
					</div>

					<div className="reservation-detail-content">
						<div className="reservation-detail-card">
							<div className="reservation-detail-icon purple"><i className="uil uil-estate"></i></div>
							<div className="reservation-detail-text">
								<div className="reservation-detail-label">{t('hall')} / {t('table')}</div>
								<div className="reservation-detail-value">
									{selectedItem.table_id
										? <>{selectedItem.hall_name || '—'} / {selectedItem.table_name || '—'}</>
										: t('without_table')
									}
								</div>
							</div>
						</div>

						<div className="d-flex gap-2">
							<div className="reservation-detail-card flex-fill">
								<div className="reservation-detail-icon green"><i className="uil uil-calendar-alt"></i></div>
								<div className="reservation-detail-text">
									<div className="reservation-detail-label">{t('date')}</div>
									<div className="reservation-detail-value">{formatDate(selectedItem.reservation_date)}</div>
								</div>
							</div>
							<div className="reservation-detail-card flex-fill">
								<div className="reservation-detail-icon orange"><i className="uil uil-clock"></i></div>
								<div className="reservation-detail-text">
									<div className="reservation-detail-label">{t('time')}</div>
									<div className="reservation-detail-value">
										{selectedItem?.reservation_start_time?.substring(0, 5) || '—'}
										{!!selectedItem?.reservation_end_time &&
											<> - {selectedItem.reservation_end_time.substring(0, 5)}</>
										}
									</div>
								</div>
							</div>
						</div>

						<div className="reservation-detail-card align-items-start">
							<div className="reservation-detail-icon blue"><i className="uil uil-user"></i></div>
							<div className="reservation-detail-text w-100">
								<div className="reservation-detail-label">{t('client')} · {t('guests')}: {selectedItem.people_number || 1}</div>
								<div className="reservation-detail-value">{selectedItem.client_name || '—'}</div>
								{!!selectedItem.client_phone &&
									<div className="reservation-detail-phone">
										<span><i className="uil uil-phone me-1"></i>{regexPhoneNumber(selectedItem.client_phone)}</span>
										<button type="button" className="reservation-detail-copy" onClick={() => copyPhone()}>
											<i className="uil uil-copy"></i>
											{t('copy')}
										</button>
									</div>
								}
							</div>
						</div>

						<div className="reservation-detail-meta">
							<div className="reservation-detail-meta-row">
								<i className="uil uil-info-circle"></i>
								<span>{t('status')}:</span>
								<b className={releasedStatuses.includes(selectedItem.status) ? 'text-danger' : ''}>{reservationStatusLabel(t, selectedItem.status)}</b>
							</div>
							{!!reservationSourceLabel(t, selectedItem) &&
								<div className="reservation-detail-meta-row">
									<i className="uil uil-link"></i>
									<span>{t('reservation_source')}:</span>
									<b>{reservationSourceLabel(t, selectedItem)}</b>
								</div>
							}
							{!!selectedItem.cancel_reason &&
								<div className="reservation-detail-meta-row">
									<i className="uil uil-info-circle"></i>
									<span>{t('reservation_reason')}:</span>
									<b>{selectedItem.cancel_reason}</b>
								</div>
							}
							<div className="reservation-detail-meta-row">
								<i className="uil uil-credit-card"></i>
								<span>{t('paid')}:</span>
								<b>{formatMoney(selectedItem.amount)}</b>
							</div>
							{!!selectedItem.note &&
								<div className="reservation-detail-meta-row">
									<i className="uil uil-comment-alt-notes"></i>
									<span>{t('note')}:</span>
									<b>{selectedItem.note}</b>
								</div>
							}
							<div className="reservation-detail-meta-row">
								<i className="uil uil-user-square"></i>
								<span>{t('employee')}:</span>
								<b>{selectedItem.created_by || '—'}</b>
							</div>
							<div className="reservation-detail-meta-row">
								<i className="uil uil-history"></i>
								<span>{t('created')}:</span>
								<b>{formatDate(selectedItem.created_at, 'dd.MM.yy HH:mm')}</b>
							</div>
						</div>
					</div>

					{declineReason !== null &&
						<div className="px-3 pb-2">
							<label className="form-label">{t('reservation_reason')}</label>
							<textarea className="form-control" rows={2} autoFocus maxLength={255}
								placeholder={t('reservation_reason_placeholder')}
								value={declineReason}
								onChange={(e) => setDeclineReason(e.target.value)} />
						</div>
					}

					<div className="reservation-detail-actions">
						{selectedItem.status === 'pending' && declineReason === null &&
							<>
								<button type="button" className="reservation-detail-btn danger" onClick={() => setDeclineReason('')}>
									<i className="uil uil-ban"></i>
									{t('reservation_decline')}
								</button>
								<button type="button" className="reservation-detail-btn" onClick={() => changeStatus('created')}>
									<i className="uil uil-check"></i>
									{t('reservation_confirm')}
								</button>
							</>
						}
						{selectedItem.status === 'pending' && declineReason !== null &&
							<button type="button" className="reservation-detail-btn danger"
								disabled={!declineReason.trim()}
								onClick={() => changeStatus('declined', declineReason)}>
								<i className="uil uil-ban"></i>
								{t('reservation_decline')}
							</button>
						}
						{selectedItem.status === 'created' &&
							<button type="button" className="reservation-detail-btn danger" onClick={() => cancelReservation()}>
								<i className="uil uil-ban"></i>
								{t('cancel_reservation')}
							</button>
						}
					</div>
				</Modal.Body>
			</Modal>
			{/* MODAL TABLE STATUS */}

			{/* MODAL CREATE */}
			<Modal show={modalCreate} animation={false} centered scrollable
				size="xl" onHide={() => setModalCreate(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('reservation_new')}</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ 'height': '560px' }}>
					<form className="reservation-form" autoComplete="off"
						onSubmit={(e) => e.preventDefault()}>
						<div className="reservation-form-left">
							<div className="form-group">
								<label>{t('phone')}</label>
								<input
									autoFocus
									type="text"
									inputMode="numeric"
									className="form-control"
									value={form.phone ?? ''}
									onChange={(e) => {
										const phone = e.target.value.replace(/\D/g, '')
										if (phone.length > 12) return
										// Номер правят — прежний гость больше не его, id подтянет поиск по новому номеру
										setForm({ ...form, 'phone': phone, 'client_id': null })
									}}
									onKeyUp={(e) => {
										if (e.key === 'Enter') searchClient()
									}}
								/>
							</div>

							<div className="form-group">
								<label>{t('client_name')}</label>
								<input
									type="text"
									className="form-control"
									value={form.client_name ?? ''}
									onChange={(e) => setForm({ ...form, 'client_name': e.target.value })}
								/>
							</div>

							<div className="form-group">
								<label>{t('guests')}<span className="required-mark">*</span></label>
								<input
									type="text"
									inputMode="numeric"
									className="form-control"
									value={form.people_number ?? ''}
									onChange={(e) => setForm({ ...form, 'people_number': e.target.value.replace(/\D/g, '').slice(0, 4) })}
								/>
							</div>

							<div className="form-group">
								<label>{t('note')}</label>
								<textarea
									className="form-control" rows={2}
									value={form.note ?? ''}
									onChange={(e) => setForm({ ...form, 'note': e.target.value })}
								/>
							</div>
						</div>

						<div className="reservation-form-middle">
							<div className="form-group">
								<label>{t('date')}</label>
								<div className="reservation-form-calendar">
									<DatePicker
										minDate={new Date()}
										selected={form.reservation_date}
										onChange={(date) => setForm({ ...form, 'reservation_date': date })}
										fixedHeight
										inline
									/>
								</div>
							</div>

							<div className="d-flex gap-2">
								<div className="form-group w-50">
									<label>{t('reservation_time_from')}</label>
									<TimeField
										value={form.reservation_start_time}
										options={timeList}
										centerOn={defaultStartTime()}
										onChange={(start) => {
											const end = form.reservation_end_time
											// Конец раньше нового начала больше не имеет смысла
											const keepEnd = end && start && timeToMinutes(end) > timeToMinutes(start)
											setForm({ ...form, 'reservation_start_time': start, 'reservation_end_time': keepEnd ? end : '' })
										}}
									/>
								</div>
								<div className="form-group w-50">
									<label>{t('reservation_time_to')}</label>
									<TimeField
										value={form.reservation_end_time}
										options={endTimeList}
										min={startMinutes}
										centerOn={minutesToTime((startMinutes ?? timeToMinutes(defaultStartTime())) + 120)}
										onChange={(end) => setForm({ ...form, 'reservation_end_time': end })}
									/>
								</div>
							</div>
						</div>

						<div className="reservation-form-tables">
							<div className="reservation-form-halls">
								{halls.map((item) => (
									<div key={item.id}
										className={`reservation-hall-item ${item.id === formHall?.id ? 'active' : ''}`}
										title={item.name}
										onClick={() => setForm({ ...form, 'hall_id': item.id, 'table_id': null, 'table_name': '' })}>
										{item.name}
									</div>
								))}
							</div>
							<div className="reservation-form-table-list">
								{/* Бронь на заведение: стол назначит касса или посадит гостя на свободный */}
								<div className={`reservation-table-tile ${!form.table_id ? 'active' : ''}`}
									onClick={() => setForm({ ...form, 'table_id': null, 'table_name': '' })}>
									<span className="reservation-table-tile-name">{t('without_table')}</span>
								</div>
								{(formHall?.tables || []).map((item) => (
									<div key={item.id}
										className={`reservation-table-tile ${item.id === form.table_id ? 'active' : ''}`}
										onClick={() => setForm({ ...form, 'hall_id': formHall.id, 'table_id': item.id, 'table_name': item.name })}>
										<span className="reservation-table-tile-name">{item.name}</span>
										{!!Number(item.guest_count) &&
											<span className="reservation-table-tile-guests" title={t('reservation_table_capacity')}>
												{item.guest_count} {t('reservation_seats')}
											</span>
										}
									</div>
								))}
							</div>
						</div>
					</form>
				</Modal.Body>
				<Modal.Footer>
					<button type="button" className="btn btn-outline-secondary reservation-modal-btn"
						onClick={() => setModalCreate(false)}>
						{t('cancel')}
					</button>
					<button type="button" className="btn btn-primary reservation-modal-btn"
						onClick={() => create()}>
						{t('save')}
					</button>
				</Modal.Footer>
			</Modal>
			{/* MODAL CREATE */}
		</>
	)
}

export default Index
